import React from 'react';
import { Offer } from 'merchant-api/types';
import {
  Card,
  FormattedMessageDiv,
  FormattedNumberDiv,
} from '@odmonorepo/core-components';
import { Row, Stack } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { Tooltip } from '@odmonorepo/core-components';
import { TLDetails } from './TLDetails';
import { TLCostAndFees } from './TLCostAndFees';
import { PartnerNameCard } from './PartnerNameCard';

interface TermLoanOfferProps {
  offer: Offer;
  applicationType: string;
  showTermsAndAgreementsMessage?: boolean;
  partnerName?: string;
}

const TermLoanOfferCard = ({ offer, applicationType, showTermsAndAgreementsMessage, partnerName }: TermLoanOfferProps) => {
  const intl = useIntl();
  const ppdValue = offer.prePaymentDiscountPct === 100 ? 100 : 25;

  const amountType =
    applicationType === 'NEW' || offer.pricedDetails.remainingPrincipal === 0
      ? 'disbursementAmt'
      : 'netAmt';

  return (
    <Card
      data-testid="term-loan-container"
      className="text-center"
      style={{ maxWidth: '600px', margin: 'auto', width: '100%' }}
      bodyClassName="p-md-5 px-md-8 p-4"
    >
      { showTermsAndAgreementsMessage && (
        <div className='pb-4 text-start'>
          <FormattedMessage id="termsAndAgreements.subtitle" />
          <sup>†</sup>
        </div>
      )}

      <Row className="mb-6 pb-0">
        <FormattedNumberDiv
          className="h1"
          value={offer.pricedDetails.lineAmount}
          style="currency"
          currency="USD"
          minimumFractionDigits={0}
        />
        <FormattedMessageDiv className="fw-bold pb-2" id={`offerReview.totalLoan`} />
        {applicationType === 'NEW' ? null : (
          <FormattedNumberDiv
          className='fs-7'
          value={offer.pricedDetails.disbursementAmount}
          currency="USD"
          style="currency"
          minimumFractionDigits={0}
          prefix={intl.formatMessage({
            id: 'offerReview.totalLoan.prefix',
          })}
          suffix={intl.formatMessage({
            id: 'offerReview.totalLoan.suffix',
          })}
          />
        )}
        <div>
          <hr className="border border-gray my-5 border-opacity-75" />
        </div>
        <FormattedNumberDiv
          className="h1"
          value={offer.pricedDetails.payment}
          style="currency"
          currency="USD"
        />
        <FormattedMessageDiv
          id={`offerReview.paymentFrequency.${offer.pricedDetails.paymentFrequency}`}
          className="fw-bold pb-2"
        />
        <div className="justify-content-center">
          <FormattedNumberDiv
            className='fs-7 d-inline'
            value={offer.pricedDetails.averageMonthlyPayment}
            currency="USD"
            style="currency"
            minimumFractionDigits={0}
            prefix={intl.formatMessage({
            id: 'offerReview.paymentFrequency.1',
            })}
            suffix={intl.formatMessage({
              id: 'offerReview.paymentFrequency.2',
            },
            { term: offer.pricedDetails.term },
            )}
          />
          <Tooltip
            title={intl.formatMessage({
            id: `offerReview.paymentFrequency.tooltip.${offer.pricedDetails.paymentFrequency}`,
            })} className="d-inline"
          />
        </div>
      </Row>
      <Stack gap={5}>
        <PartnerNameCard partnerName={partnerName} />
        <TLDetails offer={offer} amountType={amountType}/>
        <TLCostAndFees offer={offer} ppdValue={ppdValue} />
      </Stack>
      <Row className="mt-5 text-start gap-3">
      <FormattedMessageDiv
        className="fw-bold fs-6"
        id={`prepayment.reduction.ppd.${ppdValue}.label`}
      />
      <FormattedMessageDiv className="fs-7" id={`prepayment.tooltip.ppd.${ppdValue}`} />
      </Row>
    </Card>
  );
};

export default TermLoanOfferCard;
